import React, {useState, useEffect, useCallback} from 'react';
import axios from 'axios';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import Swal from 'sweetalert2';
import SensitiveDeleteModal from '../../components/SensitiveDeleteModal';
import {cancelDossierWithConfirmation, isDossierCancelable, showDossierDeleteBlockedNotice} from './dossierCancellation';
import ReclamationFormModal from '../reclamations/ReclamationFormModal';
import ReclamationDetailModal from '../reclamations/ReclamationDetailModal';
import {usePermissions} from '../../utils/usePermissions';
import {
  validateClientForm as validateClientFormUtil,
  validateDossierForm as validateDossierFormUtil,
  extractBackendError,
  mapBackendErrorToField
} from '../../utils/clientDossierValidator';
import { getStegOptionsList } from '../../utils/stegOptions';
import './DetailClient.css';

const DetailClient = ({clientId, onNavigate, onLogout}) => {
  const {hasPermission} = usePermissions();
  const [client, setClient] = useState(null);
  const [activeTab, setActiveTab] = useState('infos');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [editFieldErrors, setEditFieldErrors] = useState({});
  const [dossierFieldErrors, setDossierFieldErrors] = useState({});

  // États pour les réclamations
  const [clientReclamations, setClientReclamations] = useState([]);
  const [showAddReclamationModal, setShowAddReclamationModal] = useState(false);
  const [selectedReclamationDetail, setSelectedReclamationDetail] = useState(null);

  // État pour modification du client
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [editClientForm, setEditClientForm] = useState({
    nom: '', prenom: '', typeIdentifiant: 'cin', cin: '', passeport: '', email: '', telephone: '', adresse: '', typeClient: ''
  });

  // États pour les dossiers
  const [showAddDossierModal, setShowAddDossierModal] = useState(false);
  // Listes pour les selects relationnels
  const [commerciauxList, setCommerciauxList] = useState([]);
  const [installateursList, setInstallateursList] = useState([]);
  const [districtsList, setDistrictsList] = useState([]);

  // États projets (MT/BT-MT uniquement)
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({ nomProjet: '', description: '', totalPuissance: '', totalMontant: '' });
  
  // États pour affectation de dossier au projet
  const [showAssignProjectModal, setShowAssignProjectModal] = useState(false);
  const [selectedDossierForProject, setSelectedDossierForProject] = useState(null);
  const [assignmentProjectId, setAssignmentProjectId] = useState('');

  // États pour détails d'un projet
  const [showProjectDetailModal, setShowProjectDetailModal] = useState(false);
  const [selectedProjectForDetail, setSelectedProjectForDetail] = useState(null);
  const [commercialAddSearch, setCommercialAddSearch] = useState('');
  const CATEGORIES_STEG = [
    'Région de Tunis',
    'Région Nord',
    'Région Nord-Ouest',
    'Région Centre',
    'Région Sfax',
    'Région Sud-Ouest',
    'Région Sud',
  ];

  const getTodayStr = () => new Date().toISOString().split('T')[0];
  const getClientDateArrivee = (clientData) => {
    if (!clientData || !Array.isArray(clientData.dossiers) || clientData.dossiers.length === 0) return '';
    const dates = clientData.dossiers
      .map(d => d && d.dateArrivDossier ? new Date(d.dateArrivDossier) : null)
      .filter(d => d && !isNaN(d.getTime()))
      .sort((a, b) => a - b);
    return dates.length > 0 ? dates[0].toISOString().split('T')[0] : '';
  };

  const normalizeDecimalInput = (value) => (value === null || value === undefined ? '' : String(value));

  const [dossierForm, setDossierForm] = useState({
    pompage: false,
    projectId: '',
    referenceSteg: '',
    puissance: 0,
    district: '',
    dateDF: '',
    moisAnnee: '',
    statut: 'nouveau',
    typePaiement: 'comptant',
    montantTotalPaiement: 0,
    montantSteg: 10000,
    acompteVerse: false,
    montantComptant: '',
    montantCredit: 0,
    nombreMoisCredit: 0,
    dateDebutCredit: '',
    modePaiementComptant: 'especes',
    datePaiementComptant: getTodayStr(),
    commercial: '',
    installateur: '',
    etatDossier: 'Déposé chez Sastra',
    dateArrivDossier: getTodayStr(),
    dateDepotSteg: '',
    dateDemandeReception: '',
    dateLivraison: '',
    dateMiseEnService: '',
    bl: '',
    facture: '',
    dateFacture: '',
    notes: '',
    packId: ''
  });

  // Calcule moisAnnee depuis une date
  const getMoisAnnee = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    return `${MOIS[d.getMonth()]} ${d.getFullYear()}`;
  };

  const calculerRepartitionPaiement = (data) => {
    const total = Math.max(0, Number(data.montantTotalPaiement) || 0);
    const nbMois = Math.max(0, parseInt(data.nombreMoisCredit, 10) || 0);
    const acompteSaisi = data.acompteVerse ? Math.max(0, Number(data.montantComptant) || 0) : 0;

    let montantSteg = 0;
    if (data.montantSteg !== undefined && data.montantSteg !== null && data.montantSteg !== '' && !isNaN(Number(data.montantSteg))) {
      montantSteg = Math.min(Math.max(0, Number(data.montantSteg)), total);
    }

    const montantAutofinancement = Number(Math.max(0, total - montantSteg).toFixed(2));

    let typeEffectif = 'comptant';
    if (montantSteg === 0) {
      typeEffectif = 'comptant';
    } else if (total > 0 && Math.abs(montantSteg - total) <= 0.01) {
      typeEffectif = 'credit';
    } else if (montantSteg > 0 && montantSteg < total) {
      typeEffectif = 'mixte';
    }

    let finalComptant = 0;
    let finalCredit = 0;

    if (nbMois > 0) {
      finalComptant = Math.min(montantAutofinancement, acompteSaisi);
      finalCredit = Math.max(0, montantAutofinancement - finalComptant);
    } else {
      finalComptant = montantAutofinancement;
      finalCredit = 0;
    }

    return {
      typePaiement: typeEffectif,
      montantTotalPaiement: Number(total.toFixed(2)),
      montantSteg: Number(montantSteg.toFixed(2)),
      montantAutofinancement: Number(montantAutofinancement.toFixed(2)),
      montantComptant: Number(finalComptant.toFixed(2)),
      montantCredit: Number(finalCredit.toFixed(2))
    };
  };

  const validerScenarioPuissanceMontant = (puissanceValue, montantTotalValue) => {
    return null;
  };

  // États pour le compte
  const [compteData, setCompteData] = useState({
    email: '',
    motDePasse: ''
  });
  const [activationMode, setActivationMode] = useState('direct');

  const API_URL = import.meta.env.VITE_API_URL;
  const getApiUrl = (endpoint) => `${API_URL}${endpoint}`;
  const getToken = () => localStorage.getItem('token');

  // États pour la suppression sensible (dossier)
  const [showSensitiveDeleteModal, setShowSensitiveDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletionConfig, setDeletionConfig] = useState({ client: { enabled: true, mode: 'SOFT_DELETE' }, dossier: { enabled: true, mode: 'SOFT_DELETE' } });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [packsList, setPacksList] = useState([]);

  // Charge la liste des commerciaux + installateurs + config suppressions + packs
  const fetchHelpers = useCallback(async () => {
    try {
      const headers = {Authorization: `Bearer ${getToken()}`};
      const [cRes, iRes, dRes, cfgRes, pRes] = await Promise.all([
        axios.get(getApiUrl('/clients/helpers/commerciaux'), {headers}),
        axios.get(getApiUrl('/clients/helpers/installateurs'), {headers}),
        axios.get(getApiUrl('/steg'), {headers}),
        axios.get(getApiUrl('/system-settings/deletion-config'), {headers}),
        axios.get(getApiUrl('/packs?limit=100&statut=actif'), {headers})
      ]);
      setCommerciauxList(cRes.data?.data || []);
      setInstallateursList(iRes.data?.data || []);
      setDistrictsList(dRes.data || []);
      if (cfgRes.data?.data) {
        setDeletionConfig(cfgRes.data.data);
      }
      const packsData = pRes.data;
      const loadedPacks = Array.isArray(packsData)
        ? packsData
        : (Array.isArray(packsData?.packs) ? packsData.packs : (Array.isArray(packsData?.data) ? packsData.data : []));
      setPacksList(loadedPacks);
    } catch (_) {/* silencieux */}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchClientReclamations = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await axios.get(getApiUrl(`/reclamations/client/${clientId}`), {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setClientReclamations(res.data || []);
    } catch (_) {}
  }, [clientId]);

  useEffect(() => {
    if (clientId) {
      fetchClient();
      fetchHelpers();
      fetchClientReclamations();
    }
  }, [clientId, fetchHelpers, fetchClientReclamations]);

  const fetchClient = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        getApiUrl(`/clients/${clientId}`),
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      const clientData = response.data.data;
      setClient(clientData);
      if (isMTClient(clientData)) fetchProjects();
      const defaultDateArrivee = getClientDateArrivee(clientData) || getTodayStr();

      setEditClientForm({
        nom: response.data.data.nom || '',
        prenom: response.data.data.prenom || '',
        typeIdentifiant: response.data.data.cin ? 'cin' : 'passeport',
        cin: response.data.data.cin || '',
        passeport: response.data.data.passeport || '',
        email: response.data.data.email || '',
        telephone: response.data.data.telephone || '',
        adresse: response.data.data.adresse || '',
        typeClient: response.data.data.typeClient || 'BT'
      });

      // Pré-remplir moisAnnee du dossierForm depuis la date d'arrivee du dossier
      const moisAnneeClient = getMoisAnnee(defaultDateArrivee);
      setDossierForm(prev => ({
        ...prev,
        moisAnnee: moisAnneeClient,
        dateArrivDossier: defaultDateArrivee
      }));
      setCompteData({
        email: response.data.data.email || '',
        motDePasse: ''
      });
    } catch (err) {
      console.error('Erreur lors du chargement du client:', err);
      setError('Erreur lors du chargement du client');
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setError(null);
    setMessage(null);
  };

  const showCommissionWarning = (warning) => {
    if (!warning?.message) return;
    const isNonConforming = warning.type === 'non_conforming_client_data' || warning.type === 'combined_warning';
    const title = isNonConforming ? 'Remarque d\'information' : 'Commission non configurée';
    Swal.fire({
      title: title,
      text: warning.message,
      icon: isNonConforming ? 'info' : 'warning',
      confirmButtonText: 'Compris',
      confirmButtonColor: '#1e3a5f'
    });
  };

  // Parse les erreurs retournées par le backend
  const parseBackendError = (err) => {
    const status = err.response?.status;
    const data = err.response?.data;

    if (status === 409) {
      const msg = data?.message || '';
      if (msg.toLowerCase().includes('passeport')) return '❌ Un client avec ce numéro de passeport existe déjà.';
      if (msg.toLowerCase().includes('cin')) return '❌ Un client avec ce CIN existe déjà.';
      if ((data?.error || '').toLowerCase().includes('e11000')) return '❌ Un conflit d\'unicité a été détecté (CIN ou passeport déjà existant).';
      return `❌ ${msg || 'Conflit de données.'}`;
    }
    if (status === 401) return '❌ Session expirée. Veuillez vous reconnecter.';
    if (status === 403) return '❌ Vous n\'avez pas la permission d\'effectuer cette action.';
    if (status === 404) return '❌ Ressource introuvable.';
    if (status === 400) {
      if (data?.errors) {
        const fieldErrors = Object.values(data.errors).map(e => `❌ ${e.message}`);
        return fieldErrors.join('\n');
      }
      if (data?.message) return `❌ ${data.message}`;
    }
    if (status >= 500) return '❌ Erreur serveur. Veuillez réessayer dans quelques instants.';
    return data?.message || '❌ Une erreur inattendue est survenue.';
  };

  // ========== GESTION DU CLIENT (ÉDITION) ==========
  const handleEditClientSubmit = async (e) => {
    e.preventDefault();
    clearMessages();

    const val = validateClientFormUtil(editClientForm, { isEdit: true });
    if (!val.isValid) {
      setEditFieldErrors(val.errors);
      setError(val.errorSummary);
      return;
    }

    try {
      const isMt = editClientForm.typeClient === 'MT';
      const payload = {
        nom: editClientForm.nom,
        prenom: editClientForm.prenom,
        email: editClientForm.email,
        telephone: editClientForm.telephone,
        adresse: editClientForm.adresse,
        typeClient: editClientForm.typeClient,
        // Identifiant XOR : envoyer uniquement si non-MT
        cin: !isMt && editClientForm.typeIdentifiant === 'cin' ? editClientForm.cin : null,
        passeport: !isMt && editClientForm.typeIdentifiant === 'passeport' ? editClientForm.passeport : null
      };
      await axios.put(
        getApiUrl(`/clients/${clientId}`),
        payload,
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setShowEditClientModal(false);
      setEditFieldErrors({});
      setMessage('Client modifié avec succès');
      fetchClient();
    } catch (err) {
      console.error('Erreur lors de la modification du client:', err);
      setError(extractBackendError(err));
      setEditFieldErrors(mapBackendErrorToField(err));
    }
  };

  const handleEditClientChange = (e) => {
    const {name, value} = e.target;
    setEditFieldErrors(prev => ({
      ...prev,
      [name]: null,
      ...(name === 'typeIdentifiant' ? { cin: null, passeport: null } : {})
    }));
    if (name === 'typeIdentifiant') {
      // Vider les deux champs identifiant quand on change de type
      setEditClientForm(prev => ({ ...prev, typeIdentifiant: value, cin: '', passeport: '' }));
    } else if (name === 'typeClient' && value === 'MT') {
      setEditClientForm(prev => ({ ...prev, typeClient: value, cin: '', passeport: '' }));
    } else {
      setEditClientForm(prev => ({...prev, [name]: value}));
    }
  };

  // ========== GESTION DES DOSSIERS ==========
  const handleAddDossier = async (e) => {
    e.preventDefault();
    clearMessages();

    const val = validateDossierFormUtil(dossierForm, { typeClient: client?.typeClient });
    if (!val.isValid) {
      setDossierFieldErrors(val.errors);
      setError(val.errorSummary);
      return;
    }

    const repartition = calculerRepartitionPaiement(dossierForm);
    const isAcompteValide = !!dossierForm.acompteVerse && (parseFloat(dossierForm.montantComptant) > 0);
    const acompteMontant = isAcompteValide ? Math.max(0, parseFloat(dossierForm.montantComptant) || 0) : 0;
    const modePaiementComptant = isAcompteValide ? (dossierForm.modePaiementComptant || 'especes') : 'especes';
    const datePaiementComptant = isAcompteValide ? (dossierForm.datePaiementComptant || dossierForm.dateArrivDossier || getTodayStr()) : null;

    const creditAmount = Math.max(0, parseFloat(repartition.montantAutofinancement) - acompteMontant);
    const nbMoisSaisi = creditAmount > 0 ? Math.max(0, parseInt(dossierForm.nombreMoisCredit, 10) || 0) : 0;
    const montantMensuelCalc = (creditAmount > 0 && nbMoisSaisi > 0) ? Math.round((creditAmount / nbMoisSaisi) * 100) / 100 : 0;

    const payload = {
      ...dossierForm,
      acompteVerse: isAcompteValide,
      pompage: dossierForm.pompage,
      puissance: parseFloat(dossierForm.puissance) || 0,
      typePaiement: repartition.typePaiement,
      montantTotalPaiement: parseFloat(repartition.montantTotalPaiement) || 0,
      montantSteg: parseFloat(repartition.montantSteg) || 0,
      montantAutofinancement: parseFloat(repartition.montantAutofinancement) || 0,
      montantComptant: acompteMontant,
      montantCredit: creditAmount,
      nombreMoisCredit: nbMoisSaisi,
      dateDebutCredit: nbMoisSaisi > 0 ? (dossierForm.dateDebutCredit || null) : null,
      modePaiementComptant: isAcompteValide ? modePaiementComptant : 'especes',
      autofinancement: {
        montantTotal: creditAmount,
        montantPaye: 0,
        nombreMois: nbMoisSaisi,
        montantMensuel: montantMensuelCalc,
        dateDebutCredit: nbMoisSaisi > 0 ? (dossierForm.dateDebutCredit || null) : null,
        montantComptantInitial: acompteMontant,
        montantComptantPaye: acompteMontant,
        modePaiementComptant: isAcompteValide ? modePaiementComptant : 'especes',
        datePaiementComptant: isAcompteValide ? datePaiementComptant : null,
        historiquePaiements: isAcompteValide ? [{
          categorie: 'comptant',
          typePaiementDetail: 'avance',
          montant: acompteMontant,
          datePaiement: datePaiementComptant,
          modePaiement: modePaiementComptant,
          statut: 'paye',
          notes: 'Acompte versé à la création du dossier'
        }] : [],
        echeancier: [],
        prochaineEcheance: { montant: 0, dateEcheance: null }
      },
      dateArrivDossier: dossierForm.dateArrivDossier || getTodayStr(),
      etatDossier: dossierForm.etatDossier || 'Déposé chez Sastra',
    };
    try {
      const response = await axios.post(
        getApiUrl(`/clients/${clientId}/dossiers`),
        payload,
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setShowAddDossierModal(false);
      setDossierFieldErrors({});
      fetchClient();
      setMessage('Dossier ajouté avec succès');
      showCommissionWarning(response.data?.warning);
      // Réinitialiser avec les valeurs par défaut
      setDossierForm({
        pompage: false,
        referenceSteg: '',
        puissance: '',
        district: '',
        dateDF: '',
        moisAnnee: '',
        typePaiement: 'comptant',
        montantTotalPaiement: '',
        acompteVerse: false,
        montantComptant: '',
        montantCredit: '',
        nombreMoisCredit: 0,
        dateDebutCredit: getTodayStr(),
        modePaiementComptant: 'especes',
        datePaiementComptant: getTodayStr(),
        commercial: '',
        installateur: '',
        etatDossier: 'Déposé chez Sastra',
        dateArrivDossier: getTodayStr(),
        dateDepotSteg: '',
        dateDemandeReception: '',
        dateLivraison: '',
        dateMiseEnService: '',
        bl: '',
        facture: '',
        dateFacture: '',
        notes: ''
      });
    } catch (err) {
      console.error('Erreur lors de l\'ajout du dossier:', err);
      console.error("Erreur complète :", err);
      console.error("Status :", err.response?.status);
      console.error("Response :", err.response?.data);
      setError(extractBackendError(err));
      setDossierFieldErrors(mapBackendErrorToField(err));
    }
  };

  const handleDeleteDossier = (dossierId) => {
    const d = client?.dossiers?.find(item => item._id === dossierId);
    setDeleteTarget({
      itemType: 'dossier',
      dossierId,
      name: `Dossier ${d?.referenceSteg || ''}`
    });
    setShowSensitiveDeleteModal(true);
  };

  const handleConfirmSensitiveDelete = async (reasonArg, modeArg) => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      const reason = typeof reasonArg === 'object' ? reasonArg.reason : reasonArg;
      const mode = typeof reasonArg === 'object' ? reasonArg.mode : (modeArg || 'SOFT_DELETE');

      const response = await axios.delete(
        getApiUrl(`/clients/${clientId}/dossiers/${deleteTarget.dossierId}`),
        { data: { reason, mode }, headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setShowSensitiveDeleteModal(false);
      Swal.fire({
        icon: 'success',
        title: 'Dossier supprimé',
        text: response.data.message || 'Le dossier a été supprimé / archivé avec succès.',
        confirmButtonColor: '#1e3a5f'
      });
      fetchClient();
    } catch (err) {
      console.error('Erreur suppression dossier:', err);
      const msg = err.response?.data?.message || 'Erreur lors de la suppression du dossier';
      Swal.fire('Erreur', msg, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };
  const handleViewDossier = (dossierId) => {
    onNavigate('detail-dossier', {clientId, dossierId});
  };

  const handleCancelDossier = async (dossier) => {
    await cancelDossierWithConfirmation({
      clientId,
      dossierId: dossier._id,
      dossierLabel: dossier.referenceSteg || 'Dossier Pompage',
      getApiUrl,
      getToken,
      onSuccess: async () => {
        await fetchClient();
        if (isMTClient(client)) await fetchProjects();
      }
    });
  };

  const handleOpenAddDossierToProject = (projectId) => {
    setDossierForm(prev => ({
      ...prev,
      projectId: projectId || '',
      referenceSteg: '',
      puissance: '',
      montantTotalPaiement: ''
    }));
    setShowAddDossierModal(true);
  };

  const handleOpenProjectDetail = (project) => {
    setSelectedProjectForDetail(project);
    setShowProjectDetailModal(true);
  };

  // ========== GESTION DES PROJETS (MT/BT-MT) ==========
  const isMTClient = (c) => c && ['MT', 'BT/MT'].includes(c.typeClient);

  const fetchProjects = async () => {
    setProjectsLoading(true);
    try {
      const res = await axios.get(
        getApiUrl(`/clients/${clientId}/projects`),
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setProjects(res.data.data || []);
    } catch (_) {
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleOpenCreateProject = () => {
    setEditingProject(null);
    setProjectForm({ nomProjet: '', description: '', totalPuissance: '', totalMontant: '' });
    setShowProjectModal(true);
  };

  const handleOpenEditProject = (project) => {
    setEditingProject(project);
    setProjectForm({ 
      nomProjet: project.nomProjet, 
      description: project.description || '',
      totalPuissance: project.totalPuissance || '',
      totalMontant: project.totalMontant || ''
    });
    setShowProjectModal(true);
  };

  const handleSubmitProject = async (e) => {
    e.preventDefault();
    clearMessages();
    try {
      if (editingProject) {
        await axios.put(
          getApiUrl(`/clients/${clientId}/projects/${editingProject._id}`),
          projectForm,
          {headers: {Authorization: `Bearer ${getToken()}`}}
        );
        setMessage('Projet mis à jour avec succès');
      } else {
        await axios.post(
          getApiUrl(`/clients/${clientId}/projects`),
          projectForm,
          {headers: {Authorization: `Bearer ${getToken()}`}}
        );
        setMessage('Projet créé avec succès');
      }
      setShowProjectModal(false);
      fetchProjects();
    } catch (err) {
      setError(parseBackendError(err));
    }
  };

  const handleDeleteProject = async (projectId, nomProjet) => {
    if (!window.confirm(`Supprimer le projet « ${nomProjet} » ?\nCette action est impossible si des dossiers y sont rattachés.`)) return;
    clearMessages();
    try {
      await axios.delete(
        getApiUrl(`/clients/${clientId}/projects/${projectId}`),
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setMessage('Projet supprimé');
      fetchProjects();
    } catch (err) {
      setError(parseBackendError(err));
    }
  };

  const handleOpenAssignProject = (dossier) => {
    setSelectedDossierForProject(dossier);
    setAssignmentProjectId(dossier.projectId ? (dossier.projectId._id || dossier.projectId) : '');
    setShowAssignProjectModal(true);
  };

  const handleAssignProject = async (e) => {
    e.preventDefault();
    if (!selectedDossierForProject) return;

    const { value: confirmed } = await Swal.fire({
      title: 'Confirmer l\'affectation',
      text: `Voulez-vous affecter le dossier ${selectedDossierForProject.referenceSteg || 'Pompage'} au projet sélectionné ?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#6366f1',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Oui, affecter',
      cancelButtonText: 'Annuler'
    });

    if (!confirmed) return;

    clearMessages();
    try {
      // 1. Mettre à jour le dossier avec le nouveau projectId
      await axios.put(
        getApiUrl(`/clients/${clientId}/dossiers/${selectedDossierForProject._id}`),
        { projectId: assignmentProjectId || null },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      // 2. Ajouter un log au projet si un projet est sélectionné
      if (assignmentProjectId) {
        const projectName = getProjectName(assignmentProjectId);
        await axios.post(
          getApiUrl(`/clients/${clientId}/projects/${assignmentProjectId}/notes`),
          { text: `Affectation du dossier ${selectedDossierForProject.referenceSteg || 'Pompage'} au projet.` },
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
      }

      await Swal.fire({
        title: 'Succès !',
        text: 'Le dossier a été affecté au projet avec succès.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });

      setShowAssignProjectModal(false);
      fetchClient();
      fetchProjects();
    } catch (err) {
      console.error('Erreur lors de l\'affectation:', err);
      Swal.fire('Erreur', parseBackendError(err), 'error');
    }
  };

  const getProjectName = (projectId) => {
    if (!projectId) return null;
    const p = projects.find(pr => pr._id === (projectId._id || projectId));
    return p ? p.nomProjet : null;
  };

  const getEtatDossierStyle = (etat) => {
    const map = {
      'Déposé chez Sastra':                        { background: '#f1f5f9', color: '#475569' },
      "En attente d'approbation bureau d'étude":   { background: '#fef9c3', color: '#854d0e' },
      "Approuvé bureau d'étude":                   { background: '#dcfce7', color: '#166534' },
      "En attente d'approbation":                  { background: '#fef3c7', color: '#92400e' },
      "En attente d'installation":                 { background: '#dbeafe', color: '#1e40af' },
      "En attente de réception":                   { background: '#ede9fe', color: '#5b21b6' },
      'Réceptionné':                               { background: '#d1fae5', color: '#065f46' },
      'Manque PV':                                 { background: '#fee2e2', color: '#991b1b' },
      'Dossier en facturation':                    { background: '#e0f2fe', color: '#0369a1' },
    };
    return map[etat] || { background: '#f8fafc', color: '#64748b' };
  };

  const getStatutBadge = (statut) => {
    const badges = {
      'nouveau': 'status-badge-nouveau',
      'installe': 'status-badge-installe',
      'receptionne': 'status-badge-receptionne'
    };
    return badges[statut] || 'status-badge-default';
  };

  const repartitionPreview = calculerRepartitionPaiement(dossierForm);

  // ========== GESTION DU COMPTE ==========
  const handleCreateCompte = async () => {
    if (!compteData.email) {
      setError('L\'email est obligatoire');
      return;
    }

    if (activationMode === 'direct' && !compteData.motDePasse) {
      setError('Le mot de passe est obligatoire en mode création directe');
      return;
    }

    if (activationMode === 'direct' && compteData.motDePasse.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    clearMessages();

    try {
      const endpoint = activationMode === 'direct'
        ? `/clients/${clientId}/compte/creer`
        : `/clients/${clientId}/compte/activer`;

      const payload = activationMode === 'direct'
        ? {email: compteData.email, motDePasse: compteData.motDePasse}
        : {email: compteData.email};

      const response = await axios.post(
        getApiUrl(endpoint),
        payload,
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );

      setMessage(response.data.message || 'Compte créé avec succès');
      fetchClient();
    } catch (err) {
      console.error('Erreur lors de la création du compte:', err);
      setError(parseBackendError(err));
    }
  };

  const handleUpdateEmail = async () => {
    if (!compteData.email) {
      setError('L\'email est obligatoire');
      return;
    }

    clearMessages();

    try {
      const response = await axios.put(
        getApiUrl(`/clients/${clientId}/compte/email`),
        {email: compteData.email},
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );

      setMessage(response.data.message || 'Email mis à jour avec succès');
      fetchClient();
    } catch (err) {
      console.error('Erreur lors de la mise à jour de l\'email:', err);
      setError(parseBackendError(err));
    }
  };

  const formatDate = (date) => {
    return date ? new Date(date).toLocaleDateString('fr-FR') : 'Non renseigné';
  };

  const formatUserDisplay = (user) => {
    if (!user) return 'Utilisateur supprimé';
    if (typeof user === 'string') return 'Utilisateur supprimé';

    const prenom = user.prenom ? String(user.prenom) : '';
    const nom = user.nom ? String(user.nom) : '';
    const fullName = `${prenom} ${nom}`.trim();

    if (fullName) return fullName;
    if (user.mail) return String(user.mail);
    if (user.typeUser) return String(user.typeUser);
    return 'Utilisateur inconnu';
  };

  if (loading) {
    return (
      <div className="client-detail-premium-container">
        <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} />
        <div className="client-detail-premium-content">
          <LoadingSpinner message="Chargement des informations du client..." />
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="client-detail-premium-container">
        <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} />
        <div className="client-detail-premium-content">
          <div className="premium-error">Client non trouvé</div>
        </div>
      </div>
    );
  }

  return (
    <div className="client-detail-premium-container">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} />

      <div className="client-detail-premium-content">
        {/* Header Premium avec informations essentielles */}
        <div className="premium-client-header">
          <button
            className="premium-back-btn"
            onClick={() => onNavigate('clientsmanagement')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Retour
          </button>

          <div className="premium-client-identity">
            <div className="client-avatar">
              <span>{(client.prenom?.[0] || '') + (client.nom?.[0] || '')}</span>
            </div>
            <div className="client-main-info">
              <h1 className="client-name">{client.prenom} {client.nom}</h1>
              <div className="client-meta">
                <span className="meta-item" title="Email du client">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  {client.email || 'Non renseigné'}
                </span>
                <span className="meta-item" title="Téléphone du client">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  {client.telephone}
                </span>
                <span className="meta-item" title="Date d'arrivée réelle du dossier">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  Date arrivée dossier: {formatDate(getClientDateArrivee(client))}
                </span>
                <span className="meta-item" title="Date d'enregistrement dans le logiciel">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  Insertion Système: {new Date(client.createdAt).toLocaleDateString('fr-FR')}
                </span>
              </div>
            </div>
          </div>

          <div className="premium-quick-stats">
            <div className="stat-card">
              <div className="stat-icon stat-icon-dossiers">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-value">{client.dossiers?.length || 0}</div>
                <div className="stat-label">Dossiers</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon stat-icon-account">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-value">{client.userId ? 'Actif' : 'Inactif'}</div>
                <div className="stat-label">Compte</div>
              </div>
            </div>
          </div>
        </div>

        {/* Messages d'alerte */}
        {error && (
          <div className="premium-alert premium-alert-error">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        )}
        {message && (
          <div className="premium-alert premium-alert-success">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span>{message}</span>
          </div>
        )}

        {/* Onglets Navigation Premium */}
        <div className="premium-tabs-container">
          <div className="premium-tabs-navigation">
            <button
              className={`premium-tab ${activeTab === 'infos' ? 'active' : ''}`}
              onClick={() => setActiveTab('infos')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span>Informations Client</span>
            </button>
            {!isMTClient(client) && (
              <button
                className={`premium-tab ${activeTab === 'dossiers' ? 'active' : ''}`}
                onClick={() => setActiveTab('dossiers')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
                <span>Dossiers</span>
                {client.dossiers && client.dossiers.length > 0 && (
                  <span className="tab-count">{client.dossiers.length}</span>
                )}
              </button>
            )}
            {isMTClient(client) && (
              <button
                className={`premium-tab ${activeTab === 'projets' ? 'active' : ''}`}
                onClick={() => setActiveTab('projets')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
                <span>Projets MT</span>
                {projects.length > 0 && (
                  <span className="tab-count">{projects.length}</span>
                )}
              </button>
            )}
            <button
              className={`premium-tab ${activeTab === 'compte' ? 'active' : ''}`}
              onClick={() => setActiveTab('compte')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>Compte</span>
            </button>
            <button
              className={`premium-tab ${activeTab === 'reclamations' ? 'active' : ''}`}
              onClick={() => setActiveTab('reclamations')}
            >
              <span style={{ fontSize: '1.1rem' }}>⚡</span>
              <span>Réclamations Client</span>
              {clientReclamations.length > 0 && (
                <span className="tab-count" style={{ background: '#ef4444', color: 'white' }}>{clientReclamations.length}</span>
              )}
            </button>
          </div>
        </div>

        {/* Contenu des onglets */}
        <div className="premium-tab-content">
          {/* ONGLET INFORMATIONS CLIENT */}
          {activeTab === 'infos' && (
            <div className="premium-info-section">
              <div className="premium-section-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <div>
                  <h2 className="section-title">Informations Personnelles</h2>
                  <p className="section-subtitle">Détails et coordonnées du client</p>
                </div>
               {!showEditClientModal && (
                  <button
                    className="premium-btn-add"
                    style={{backgroundColor: '#f39c12', color: '#fff'}}
                    onClick={() => setShowEditClientModal(true)}
                  >
                    ✏️ Modifier client
                  </button>
                )}
              </div>

              <div className="info-cards-grid">
                  {/* Carte Identité */}
                  <div className="premium-info-card">
                    <div className="info-card-header">
                      <div className="info-card-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </div>
                      <h3>Identité</h3>
                    </div>
                    <div className="info-card-body">
                      <div className="info-row">
                        <span className="info-label">Nom complet</span>
                        <span className="info-value">{client.prenom} {client.nom}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">{client.cin ? 'CIN' : 'Passeport'}</span>
                        <span className="info-value info-value-highlight">{client.cin || client.passeport || '—'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Date arrivée dossier</span>
                        <span className="info-value text-muted" style={{fontSize: '13px'}} title="Date d'arrivée du dossier physique">
                          {(() => {
                            const src = getClientDateArrivee(client);
                            if (!src) return '-';
                            const d = new Date(src);
                            return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Carte Audit / Historique */}
                  <div className="premium-info-card">
                    <div className="info-card-header">
                      <div className="info-card-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                        </svg>
                      </div>
                      <h3>Historique</h3>
                    </div>
                    <div className="info-card-body">
                      <div className="info-row">
                        <span className="info-label">Ajouté par</span>
                        <span className="info-value" style={{fontSize: '13px'}}>
                          {client.createdBy ? formatUserDisplay(client.createdBy) : <em className="text-muted">Système</em>}
                        </span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Insertion Système</span>
                        <span className="info-value text-muted" style={{fontSize: '13px', fontStyle: 'italic'}}>
                          {client.createdAt ? new Date(client.createdAt).toLocaleDateString('fr-FR', {
                            day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          }) : '-'}
                        </span>
                      </div>
                      {client.updatedBy && (
                        <>
                          <div className="info-row">
                            <span className="info-label">Modifié par</span>
                            <span className="info-value" style={{fontSize: '13px', color: '#f39c12'}}>
                              {formatUserDisplay(client.updatedBy)}
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Dernière modification</span>
                            <span className="info-value text-muted" style={{fontSize: '13px'}}>
                              {client.lastUpdatedAt ? new Date(client.lastUpdatedAt).toLocaleDateString('fr-FR', {
                                day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                              }) : (client.updatedAt ? new Date(client.updatedAt).toLocaleDateString('fr-FR', {
                                day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                              }) : '-')}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Carte Contact */}
                  <div className="premium-info-card">
                    <div className="info-card-header">
                      <div className="info-card-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                      </div>
                      <h3>Contact</h3>
                    </div>
                    <div className="info-card-body">
                      <div className="info-row">
                        <span className="info-label">Téléphone</span>
                        <span className="info-value">{client.telephone}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Email</span>
                        <span className="info-value">{client.email || <em className="text-muted">Non renseigné</em>}</span>
                      </div>
                    </div>
                  </div>

                  {/* Carte Localisation */}
                  <div className="premium-info-card premium-info-card-large">
                    <div className="info-card-header">
                      <div className="info-card-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                      </div>
                      <h3>Localisation</h3>
                    </div>
                    <div className="info-card-body">
                      <div className="info-row">
                        <span className="info-label">Adresse</span>
                        <span className="info-value">{client.adresse || <em className="text-muted">Non renseignée</em>}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* MODAL MODIFIER CLIENT */}
          {showEditClientModal && (
            <div className="premium-modal-overlay">
              <div className="premium-modal" onClick={(e) => e.stopPropagation()} style={{maxWidth: '800px'}}>
                <div className="premium-modal-header">
                  <h2>✏️ Modifier le client</h2>
                  <button className="premium-modal-close" onClick={() => setShowEditClientModal(false)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
                <div className="premium-modal-body">
                  <form onSubmit={handleEditClientSubmit}>
                    <div className="premium-form-grid">
                      <div className="premium-form-group">
                        <label className="premium-label">
                          Prénom {editClientForm.typeClient !== 'MT' && '*'}
                        </label>
                        <input
                          type="text"
                          name="prenom"
                          className="premium-input"
                          value={editClientForm.prenom}
                          onChange={handleEditClientChange}
                          required={editClientForm.typeClient !== 'MT'}
                          style={editFieldErrors.prenom ? { border: '1px solid #ef4444' } : {}}
                        />
                        {editFieldErrors.prenom && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{editFieldErrors.prenom}</span>}
                      </div>
                      <div className="premium-form-group">
                        <label className="premium-label">Nom *</label>
                        <input
                          type="text"
                          name="nom"
                          className="premium-input"
                          value={editClientForm.nom}
                          onChange={handleEditClientChange}
                          required
                          style={editFieldErrors.nom ? { border: '1px solid #ef4444' } : {}}
                        />
                        {editFieldErrors.nom && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{editFieldErrors.nom}</span>}
                      </div>

                      {editClientForm.typeClient !== 'MT' && (
                        <div className="premium-form-group">
                          <label className="premium-label">Identifiant *</label>
                          <div style={{display:'flex', gap:'16px', marginBottom:'8px'}}>
                            <label style={{display:'flex', alignItems:'center', gap:'6px', cursor:'pointer'}}>
                              <input type="radio" name="typeIdentifiant" value="cin"
                                checked={editClientForm.typeIdentifiant === 'cin'}
                                onChange={handleEditClientChange} />
                              CIN
                            </label>
                            <label style={{display:'flex', alignItems:'center', gap:'6px', cursor:'pointer'}}>
                              <input type="radio" name="typeIdentifiant" value="passeport"
                                checked={editClientForm.typeIdentifiant === 'passeport'}
                                onChange={handleEditClientChange} />
                              Passeport
                            </label>
                          </div>
                          {editClientForm.typeIdentifiant === 'cin' && (
                            <>
                              <input
                                type="text" name="cin"
                                className="premium-input"
                                value={editClientForm.cin}
                                onChange={handleEditClientChange}
                                inputMode="numeric" maxLength={8}
                                placeholder="8 chiffres (ex: 05260081)"
                                style={editFieldErrors.cin ? { border: '1px solid #ef4444' } : {}}
                              />
                              {editFieldErrors.cin && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{editFieldErrors.cin}</span>}
                            </>
                          )}
                          {editClientForm.typeIdentifiant === 'passeport' && (
                            <>
                              <input
                                type="text" name="passeport"
                                className="premium-input"
                                value={editClientForm.passeport}
                                onChange={handleEditClientChange}
                                maxLength={30}
                                placeholder="Numéro de passeport"
                                style={editFieldErrors.passeport ? { border: '1px solid #ef4444' } : {}}
                              />
                              {editFieldErrors.passeport && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{editFieldErrors.passeport}</span>}
                            </>
                          )}
                        </div>
                      )}
                      <div className="premium-form-group">
                        <label className="premium-label">Type Client</label>
                        <select
                          name="typeClient"
                          className="premium-input premium-select"
                          value={editClientForm.typeClient}
                          onChange={handleEditClientChange}
                        >
                          <option value="BT">BT (Basse Tension)</option>
                          <option value="MT">MT (Moyenne Tension)</option>
                          <option value="BT/MT">BT/MT (Basse & Moyenne Tension)</option>
                        </select>
                      </div>

                      <div className="premium-form-group">
                        <label className="premium-label">Téléphone</label>
                        <input
                          type="text"
                          name="telephone"
                          className="premium-input"
                          value={editClientForm.telephone}
                          onChange={handleEditClientChange}
                          style={editFieldErrors.telephone ? { border: '1px solid #ef4444' } : {}}
                        />
                        {editFieldErrors.telephone && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{editFieldErrors.telephone}</span>}
                      </div>
                      <div className="premium-form-group">
                        <label className="premium-label">Email</label>
                        <input
                          type="email"
                          name="email"
                          className="premium-input"
                          value={editClientForm.email}
                          onChange={handleEditClientChange}
                          style={editFieldErrors.email ? { border: '1px solid #ef4444' } : {}}
                        />
                        {editFieldErrors.email && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{editFieldErrors.email}</span>}
                      </div>

                      <div className="premium-form-group premium-form-group-full">
                        <label className="premium-label">Adresse complète</label>
                        <textarea
                          name="adresse"
                          className="premium-input premium-textarea"
                          value={editClientForm.adresse}
                          onChange={handleEditClientChange}
                          rows="2"
                        ></textarea>
                      </div>

                      <div className="premium-form-group">
                        <label className="premium-label">
                          Insertion système
                          <span style={{fontSize: '11px', color: '#9ca3af', fontWeight: 400, marginLeft: '6px'}}>(non modifiable)</span>
                        </label>
                        <input
                          type="text"
                          className="premium-input"
                          value={client?.createdAt ? new Date(client.createdAt).toLocaleDateString('fr-FR', {
                            day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          }) : '—'}
                          disabled
                          style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280', fontStyle: 'italic' }}
                          title="Date technique d'insertion dans la base de données — jamais modifiable"
                        />
                      </div>
                    </div>

                    <div className="premium-modal-footer" style={{marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '15px 0 0', borderTop: '1px solid #e2e8f0', background: 'transparent'}}>
                      <button type="button" className="premium-btn-secondary" onClick={() => setShowEditClientModal(false)}>
                        Annuler
                      </button>
                      <button type="submit" className="premium-btn-primary">
                        Enregistrer les modifications
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* ONGLET DOSSIERS */}
          {activeTab === 'dossiers' && (
            <div className="premium-dossiers-section">
              <div className="premium-section-header">
                <div>
                  <h2 className="section-title">Dossiers Client</h2>
                  <p className="section-subtitle">Gestion des dossiers photovoltaïques</p>
                </div>
                <button
                  className="premium-btn-add"
                  onClick={() => setShowAddDossierModal(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Nouveau Dossier
                </button>
              </div>

              {client.dossiers && client.dossiers.length > 0 ? (
                <div className="dossiers-premium-grid">
                  {client.dossiers.map((dossier) => (
                    <div key={dossier._id} className="premium-dossier-card" style={dossier.pompage ? {borderLeft: '4px solid #0284c7', backgroundColor: '#f0f9ff'} : {}}>
                      <div className="dossier-card-header">
                        <div className="dossier-ref" style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                          <span>{dossier.referenceSteg || 'Dossier Pompage'}</span>
                          {dossier.pompage && (
                            <span style={{padding: '2px 6px', fontSize: '10px', backgroundColor: '#e0f2fe', color: '#0284c7', borderRadius: '4px', border: '1px solid #bae6fd', fontWeight: 'bold'}}>POMPAGE</span>
                          )}
                          {getProjectName(dossier.projectId) && (
                            <span style={{padding:'2px 8px',fontSize:'10px',background:'#e0e7ff',color:'#4338ca',borderRadius:'10px',fontWeight:'600'}}>
                              📁 {getProjectName(dossier.projectId)}
                            </span>
                          )}
                        </div>
                        <span className="premium-badge" style={getEtatDossierStyle(dossier.etatDossier)}>
                          {dossier.etatDossier || '—'}
                        </span>
                      </div>

                      <div className="dossier-card-body">
                        <div className="dossier-detail-row">
                          <span className="detail-label">Puissance</span>
                          <span className="detail-value detail-value-accent">{dossier.puissance} kWc</span>
                        </div>
                        <div className="dossier-detail-row">
                          <span className="detail-label">District</span>
                          <span className="detail-value">
                            {dossier.district
                              ? (typeof dossier.district === 'object' ? dossier.district.region : dossier.district)
                              : <em className="text-muted">Non renseigné</em>}
                          </span>
                        </div>
                        <div className="dossier-detail-row">
                          <span className="detail-label">Période</span>
                          <span className="detail-value">{dossier.moisAnnee || '—'}</span>
                        </div>
                        <div className="dossier-detail-row">
                          <span className="detail-label">Commercial</span>
                          <span className="detail-value">
                            {dossier.commercial
                              ? (typeof dossier.commercial === 'object'
                                ? `${dossier.commercial.prenom || ''} ${dossier.commercial.nom || ''}`.trim()
                                : dossier.commercial)
                              : <em className="text-muted">Non assigné</em>}
                          </span>
                        </div>
                        <div className="dossier-detail-row">
                          <span className="detail-label">Insertion</span>
                          <span className="detail-value" style={{fontSize: '11px', fontStyle: 'italic', color: '#6b7280'}}>
                            {dossier.createdAt ? new Date(dossier.createdAt).toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'}) : '—'}
                          </span>
                        </div>
                      </div>

                      <div className="dossier-card-footer">
                        <button
                          className="premium-btn-secondary"
                          onClick={() => handleViewDossier(dossier._id)}
                        >
                          Voir détails
                        </button>
                        {isMTClient(client) && (
                          <button
                            className="premium-btn-secondary"
                            style={{borderColor: '#6366f1', color: '#6366f1'}}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenAssignProject(dossier);
                            }}
                          >
                            Affecter au projet
                          </button>
                        )}
                        {isDossierCancelable(dossier) && (
                          <button
                            className="premium-btn-warning"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelDossier(dossier);
                            }}
                          >
                            Annuler
                          </button>
                        )}
                        <button
                          className="premium-btn-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDossier(dossier._id);
                          }}
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="premium-empty-state">
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                  </svg>
                  <h3>Aucun dossier</h3>
                  <p>Ce client n'a pas encore de dossier</p>
                  <button
                    className="premium-btn-primary"
                    onClick={() => setShowAddDossierModal(true)}
                  >
                    Créer le premier dossier
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ONGLET COMPTE */}
          {activeTab === 'compte' && (
            <div className="premium-compte-section">
              <div className="premium-section-header">
                <h2 className="section-title">Gestion du Compte Client</h2>
                <p className="section-subtitle">Configuration de l'accès client</p>
              </div>

              {client.userId ? (
                <div className="compte-actif-premium">
                  <div className="premium-success-box">
                    <div className="success-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </div>
                    <div className="success-content">
                      <h3>Compte Actif</h3>
                      <p>Le client peut se connecter avec son email : <strong>{client.email}</strong></p>
                    </div>
                  </div>

                  <div className="premium-form-card">
                    <h3 className="form-card-title">Modifier l'email</h3>
                    <div className="premium-form-group">
                      <label className="premium-label">Nouvel email</label>
                      <div className="input-button-group">
                        <input
                          type="email"
                          className="premium-input"
                          value={compteData.email}
                          onChange={(e) => setCompteData({...compteData, email: e.target.value})}
                          placeholder="nouveau@email.com"
                        />
                        <button className="premium-btn-primary" onClick={handleUpdateEmail}>
                          Mettre à jour
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="compte-inactif-premium">
                  <div className="premium-warning-box">
                    <div className="warning-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                    </div>
                    <div className="warning-content">
                      <h3>Aucun Compte Actif</h3>
                      <p>Ce client n'a pas encore de compte pour accéder à l'espace client</p>
                    </div>
                  </div>

                  <div className="premium-form-card">
                    <h3 className="form-card-title">Créer un compte client</h3>

                    <div className="premium-radio-group">
                      <label className="premium-radio-option">
                        <input
                          type="radio"
                          value="direct"
                          checked={activationMode === 'direct'}
                          onChange={(e) => setActivationMode(e.target.value)}
                        />
                        <div className="radio-content">
                          <strong>Création directe</strong>
                          <small>Vous définissez le mot de passe immédiatement</small>
                        </div>
                      </label>
                      <label className="premium-radio-option">
                        <input
                          type="radio"
                          value="link"
                          checked={activationMode === 'link'}
                          onChange={(e) => setActivationMode(e.target.value)}
                        />
                        <div className="radio-content">
                          <strong>Lien d'activation</strong>
                          <small>Le client reçoit un email pour créer son mot de passe</small>
                        </div>
                      </label>
                    </div>

                    <div className="premium-form-group">
                      <label className="premium-label">Email *</label>
                      <input
                        type="email"
                        className="premium-input"
                        value={compteData.email}
                        onChange={(e) => setCompteData({...compteData, email: e.target.value})}
                        placeholder="client@email.com"
                        required
                      />
                    </div>

                    {activationMode === 'direct' && (
                      <div className="premium-form-group">
                        <label className="premium-label">Mot de passe *</label>
                        <input
                          type="password"
                          className="premium-input"
                          value={compteData.motDePasse}
                          onChange={(e) => setCompteData({...compteData, motDePasse: e.target.value})}
                          placeholder="Minimum 6 caractères"
                          minLength="6"
                        />
                        <small className="input-help">Le mot de passe doit contenir au moins 6 caractères</small>
                      </div>
                    )}

                    <button className="premium-btn-primary premium-btn-full" onClick={handleCreateCompte}>
                      {activationMode === 'direct' ? 'Créer le compte' : 'Envoyer le lien d\'activation'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ONGLET PROJETS MT */}
          {activeTab === 'projets' && isMTClient(client) && (
            <div className="premium-dossiers-section">
              <div className="premium-section-header">
                <div>
                  <h2 className="section-title">Projets MT</h2>
                  <p className="section-subtitle">Organisation des dossiers par projet</p>
                </div>
                <button className="premium-btn-add" onClick={handleOpenCreateProject}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Nouveau Projet
                </button>
              </div>

              {projectsLoading ? (
                <LoadingSpinner message="Chargement des projets..." />
              ) : projects.length === 0 ? (
                <div className="premium-empty-state">
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                  <h3>Aucun projet</h3>
                  <p>Créez un projet pour organiser les dossiers MT de ce client</p>
                  <button className="premium-btn-primary" onClick={handleOpenCreateProject}>Créer le premier projet</button>
                </div>
              ) : (
                <div className="projects-premium-grid">
                  {projects.map(project => {
                    const dossiersProjet = (client.dossiers || []).filter(
                      d => !d.isDeleted && d.projectId &&
                           (d.projectId._id || d.projectId).toString() === project._id.toString()
                    );
                    return (
                      <div key={project._id} className="premium-dossier-card project-card">
                        <div className="dossier-card-header">
                          <div className="dossier-ref" style={{display:'flex',alignItems:'center',gap:'8px'}}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
                              <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                            </svg>
                            <span style={{fontWeight:'700',color:'#4338ca'}}>{project.nomProjet}</span>
                          </div>
                          <span style={{padding:'3px 10px',borderRadius:'12px',fontSize:'11px',background:'#e0e7ff',color:'#3730a3',fontWeight:'600'}}>
                            {dossiersProjet.length} dossier{dossiersProjet.length !== 1 ? 's' : ''}
                          </span>
                        </div>

                        <div className="dossier-card-body">
                          {project.description && (
                            <div className="dossier-detail-row">
                              <span className="detail-label">Description</span>
                              <span className="detail-value" style={{fontSize:'12px',fontStyle:'italic',color:'#6b7280'}}>{project.description}</span>
                            </div>
                          )}
                          <div className="dossier-detail-row">
                            <span className="detail-label">Puissance totale</span>
                            <span className="detail-value detail-value-accent">
                              {dossiersProjet.reduce((s,d)=>s+(d.puissance||0),0).toFixed(2)} kWc
                            </span>
                          </div>
                          <div className="dossier-detail-row">
                            <span className="detail-label">Montant total</span>
                            <span className="detail-value detail-value-accent">
                              {dossiersProjet.reduce((s,d)=>s+(d.montantTotalPaiement||0),0).toLocaleString('fr-FR')} DT
                            </span>
                          </div>
                          <div className="dossier-detail-row">
                            <span className="detail-label">Créé le</span>
                            <span className="detail-value" style={{fontSize:'11px',color:'#9ca3af'}}>
                              {new Date(project.createdAt).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        </div>

                        {/* Dossiers rattachés */}
                        {dossiersProjet.length > 0 && (
                          <div className="project-inner-list">
                            <p style={{fontSize:'11px',fontWeight:'600',color:'#6b7280',margin:'0 0 8px', textTransform:'uppercase'}}>Dossiers rattachés</p>
                            {dossiersProjet.map(d => (
                              <div key={d._id} className="project-inner-item">
                                <div className="dossier-info">
                                  <span className="ref">{d.referenceSteg || 'Pompage'}</span>
                                  <span className="meta">{d.puissance} kWc</span>
                                </div>
                                <div className="project-inner-actions">
                                  <span className="premium-badge" style={{...getEtatDossierStyle(d.etatDossier), fontSize:'9px', padding:'2px 6px'}}>
                                    {d.etatDossier || '—'}
                                  </span>
                                  <div style={{display:'flex',gap:'4px'}}>
                                    <button
                                      className="premium-btn-icon"
                                      title="Voir détails"
                                      onClick={() => handleViewDossier(d._id)}
                                      style={{background:'#eff6ff',color:'#2563eb'}}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                                      </svg>
                                    </button>
                                    {isDossierCancelable(d) && (
                                      <button
                                        className="premium-btn-icon"
                                        title="Annuler"
                                        onClick={() => handleCancelDossier(d)}
                                        style={{background:'#ffedd5',color:'#c2410c'}}
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                                        </svg>
                                      </button>
                                    )}
                                    <button
                                      className="premium-btn-icon"
                                      title="Supprimer"
                                      onClick={() => handleDeleteDossier(d._id)}
                                      style={{background:'#fef2f2',color:'#dc2626'}}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="dossier-card-footer">
                          <button 
                            className="premium-btn-primary" 
                            style={{background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'}}
                            onClick={() => handleOpenProjectDetail(project)}
                          >
                            📁 Détail Projet
                          </button>
                          <button className="premium-btn-secondary" onClick={() => handleOpenEditProject(project)}>
                            ✏️ Modifier
                          </button>
                          <button
                            className="premium-btn-secondary"
                            style={{color:'#6366f1',borderColor:'#6366f1'}}
                            onClick={() => handleOpenAddDossierToProject(project._id)}
                          >
                            + Dossier
                          </button>
                          <button className="premium-btn-danger" onClick={() => handleDeleteProject(project._id, project.nomProjet)}>
                            Supprimer
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Dossiers non affectés (MT uniquement) */}
              {(() => {
                const dossiersSansProjet = (client.dossiers || []).filter(d => !d.isDeleted && !d.projectId);
                if (dossiersSansProjet.length === 0) return null;
                return (
                  <div className="premium-warning-box" style={{marginTop:'30px',borderLeft:'4px solid #f59e0b',background:'#fffbeb'}}>
                    <div className="warning-icon" style={{color:'#f59e0b'}}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                    </div>
                    <div className="warning-content">
                      <h3 style={{color:'#92400e',fontSize:'15px',marginBottom:'10px'}}>Dossiers non affectés à un projet</h3>
                      <div className="dossiers-mini-list" style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                        {dossiersSansProjet.map(d => (
                          <div key={d._id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',background:'white',padding:'8px 12px',borderRadius:'6px',boxShadow:'0 1px 2px rgba(0,0,0,0.05)'}}>
                            <div style={{display:'flex',flexDirection:'column'}}>
                              <span style={{fontSize:'12px',fontWeight:'700'}}>{d.referenceSteg || 'Pompage'}</span>
                              <span style={{fontSize:'10px',color:'#6b7280'}}>{d.puissance} kWc - {d.etatDossier}</span>
                            </div>
                            <div style={{display:'flex',gap:'8px'}}>
                              <button
                                className="premium-btn-secondary"
                                style={{fontSize:'11px',padding:'4px 10px'}}
                                onClick={() => handleViewDossier(d._id)}
                              >
                                Voir
                              </button>
                              <button
                                className="premium-btn-primary"
                                style={{fontSize:'11px',padding:'4px 10px',background:'#f59e0b'}}
                                onClick={() => handleOpenAssignProject(d)}
                              >
                                Affecter
                              </button>
                              {isDossierCancelable(d) && (
                                <button
                                  className="premium-btn-warning"
                                  style={{fontSize:'11px',padding:'4px 10px'}}
                                  onClick={() => handleCancelDossier(d)}
                                >
                                  Annuler
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* MODAL AJOUTER DOSSIER */}
        {showAddDossierModal && (
          <div className="premium-modal-overlay">
            <div className="premium-modal" onClick={(e) => e.stopPropagation()}>
              <div className="premium-modal-header">
                <h2>Nouveau Dossier</h2>
                <button className="premium-modal-close" onClick={() => setShowAddDossierModal(false)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleAddDossier} className="premium-modal-body">
                <div className="premium-form-grid">
                  <div className="premium-form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#f0f9ff', padding: '10px', borderRadius: '8px', border: '1px solid #bae6fd', gridColumn: '1 / -1' }}>
                    <input
                      type="checkbox"
                      id="pompageDossier"
                      name="pompage"
                      checked={dossierForm.pompage}
                      onChange={(e) => {
                        const isPompage = e.target.checked;
                        setDossierForm(prev => ({
                          ...prev,
                          pompage: isPompage,
                          typePaiement: isPompage ? 'comptant' : prev.typePaiement
                        }));
                      }}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label htmlFor="pompageDossier" style={{ margin: 0, cursor: 'pointer', fontWeight: 'bold', color: '#0369a1' }}>
                      Dossier Pompage (Paiement 100% Comptant, référence STEG non obligatoire)
                    </label>
                  </div>

                  {/* Sélecteur projet pour clients MT */}
                  {isMTClient(client) && (
                    <div className="premium-form-group" style={{gridColumn:'1 / -1', background:'#f5f3ff', padding:'10px', borderRadius:'8px', border:'1px solid #c4b5fd'}}>
                      <label className="premium-label" style={{color:'#4338ca'}}>
                        Projet MT *
                      </label>
                      {projects.length === 0 ? (
                        <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                          <span style={{color:'#9ca3af',fontSize:'13px',fontStyle:'italic'}}>Aucun projet existant.</span>
                          <button
                            type="button"
                            className="premium-btn-secondary"
                            style={{fontSize:'12px',padding:'4px 12px'}}
                            onClick={() => { setShowAddDossierModal(false); handleOpenCreateProject(); }}
                          >
                            + Créer un projet
                          </button>
                        </div>
                      ) : (
                        <select
                          className="premium-input premium-select"
                          value={dossierForm.projectId}
                          onChange={(e) => setDossierForm({...dossierForm, projectId: e.target.value})}
                          required
                        >
                          <option value="">— Sélectionner un projet —</option>
                          {projects.map(p => (
                            <option key={p._id} value={p._id}>{p.nomProjet}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                  <div className="premium-form-group">
                    <label className="premium-label">Référence STEG {!dossierForm.pompage && '*'}</label>
                    <input
                      type="text"
                      className="premium-input"
                      value={dossierForm.referenceSteg}
                      onChange={(e) => setDossierForm({...dossierForm, referenceSteg: e.target.value})}
                      required={!dossierForm.pompage}
                      placeholder="Ex: STEG-2024-001"
                      style={dossierFieldErrors.referenceSteg ? { border: '1px solid #ef4444' } : {}}
                    />
                    {dossierFieldErrors.referenceSteg && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{dossierFieldErrors.referenceSteg}</span>}
                  </div>

                  <div className="premium-form-group">
                    <label className="premium-label">Puissance (kWc) *</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="premium-input"
                      step="any"
                      value={dossierForm.puissance}
                      onChange={(e) => setDossierForm({...dossierForm, puissance: e.target.value})}
                      required
                      placeholder="Ex: 5.5"
                      style={dossierFieldErrors.puissance ? { border: '1px solid #ef4444' } : {}}
                    />
                    {dossierFieldErrors.puissance && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{dossierFieldErrors.puissance}</span>}
                  </div>

                  {/* Affectation du Pack Commercial */}
                  <div className="premium-form-group" style={{ gridColumn: '1 / -1', background: '#f0fdf4', padding: '14px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                    <label className="premium-label" style={{ color: '#15803d', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      📦 Affectation du Pack Commercial (Optionnel)
                    </label>
                    <select
                      className="premium-input premium-select"
                      value={dossierForm.packId || ''}
                      onChange={(e) => {
                        const pId = e.target.value;
                        if (!pId || pId === 'none') {
                          setDossierForm(prev => ({ ...prev, packId: '' }));
                          return;
                        }
                        const selectedPack = packsList.find(p => String(p._id) === String(pId));
                        if (selectedPack) {
                          const price = (selectedPack.prixFinal !== undefined && selectedPack.prixFinal !== null && Number(selectedPack.prixFinal) > 0)
                            ? Number(selectedPack.prixFinal)
                            : Number(selectedPack.prixCalcule || 0);
                          setDossierForm(prev => ({
                            ...prev,
                            packId: selectedPack._id,
                            montantTotalPaiement: price > 0 ? price : prev.montantTotalPaiement
                          }));
                        }
                      }}
                    >
                      <option value="">-- Aucun pack (Saisie libre des produits) --</option>
                      {packsList.map(pack => {
                        const powerStr = pack.puissance ? (String(pack.puissance).toLowerCase().includes('kwc') ? pack.puissance : `${pack.puissance} kWc`) : 'Puissance N/A';
                        const finalPriceNum = Number(pack.prixFinal);
                        const calcPriceNum = Number(pack.prixCalcule);
                        const priceVal = (!isNaN(finalPriceNum) && finalPriceNum > 0)
                          ? finalPriceNum
                          : ((!isNaN(calcPriceNum) && calcPriceNum > 0) ? calcPriceNum : null);
                        const priceStr = priceVal !== null ? `${priceVal.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} DT` : 'Prix non défini';
                        const clientPowerNum = parseFloat(dossierForm.puissance);
                        const isPowerMatch = !isNaN(clientPowerNum) && pack.puissance && String(pack.puissance).includes(String(Math.floor(clientPowerNum)));
                        return (
                          <option key={pack._id} value={pack._id}>
                            {pack.nom} — {powerStr} — {priceStr} {isPowerMatch ? '⭐ (Recommandé)' : ''}
                          </option>
                        );
                      })}
                    </select>
                    {dossierForm.packId && (() => {
                      const p = packsList.find(item => String(item._id) === String(dossierForm.packId));
                      if (!p) return null;
                      const finalPNum = Number(p.prixFinal);
                      const calcPNum = Number(p.prixCalcule);
                      const pPriceVal = (!isNaN(finalPNum) && finalPNum > 0) ? finalPNum : ((!isNaN(calcPNum) && calcPNum > 0) ? calcPNum : null);
                      const pPriceStr = pPriceVal !== null ? `${pPriceVal.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} DT` : 'Prix non défini';
                      const pPowerStr = p.puissance ? (String(p.puissance).toLowerCase().includes('kwc') ? p.puissance : `${p.puissance} kWc`) : 'N/A';
                      return (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#166534', background: '#ffffff', padding: '8px 12px', borderRadius: '6px', border: '1px solid #dcfce7' }}>
                          <strong>Pack sélectionné :</strong> {p.nom} | <strong>Puissance :</strong> {pPowerStr} | <strong>Prix de vente :</strong> {pPriceStr}
                        </div>
                      );
                    })()}
                  </div>

                  <div className="premium-form-group">
                    <label className="premium-label">Région STEG (Catégorie)</label>
                    <select
                      className="premium-input premium-select"
                      value={dossierForm._categorieSteg || ''}
                      onChange={(e) => setDossierForm({...dossierForm, _categorieSteg: e.target.value, district: ''})}
                    >
                      <option value="">-- Sélectionner une région --</option>
                      {CATEGORIES_STEG.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div className="premium-form-group">
                    <label className="premium-label">District (Bureau) *</label>
                    <select
                      className="premium-input premium-select"
                      value={dossierForm.district}
                      onChange={(e) => setDossierForm({...dossierForm, district: e.target.value})}
                      required
                    >
                      <option value="">-- Sélectionner un bureau --</option>
                      {districtsList
                        .filter(d => !dossierForm._categorieSteg || d.categorie === dossierForm._categorieSteg)
                        .map(d => (
                          <option key={d._id} value={d._id}>{d.region}</option>
                        ))
                      }
                    </select>
                  </div>

                  <div className="premium-form-group">
                    <label className="premium-label">Date Arrivée Dossier</label>
                    <input
                      type="date"
                      className="premium-input"
                      value={dossierForm.dateArrivDossier}
                      onChange={(e) => {
                        const val = e.target.value;
                        const moisAnnee = getMoisAnnee(val);
                        setDossierForm({...dossierForm, dateArrivDossier: val, moisAnnee});
                      }}
                    />
                  </div>

                  <div className="premium-form-group">
                    <label className="premium-label">Mois/Année (Calculé)</label>
                    <input
                      type="text"
                      className="premium-input"
                      value={dossierForm.moisAnnee}
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280' }}
                      title="Extrait automatiquement de la date d'arrivée du dossier"
                    />
                  </div>

                  <div className="premium-form-group">
                    <label className="premium-label">Montant Total Paiement (TND)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="premium-input"
                      step="any"
                      min="0"
                      value={dossierForm.montantTotalPaiement}
                      onChange={(e) => setDossierForm({...dossierForm, montantTotalPaiement: normalizeDecimalInput(e.target.value)})}
                      style={dossierFieldErrors.montantTotalPaiement ? { border: '1px solid #ef4444' } : {}}
                    />
                    {dossierFieldErrors.montantTotalPaiement && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{dossierFieldErrors.montantTotalPaiement}</span>}
                  </div>

                  <div className="premium-form-group">
                    <label className="premium-label">Montant Crédit STEG *</label>
                    <select
                      className="premium-input premium-select"
                      value={dossierForm.montantSteg !== undefined && dossierForm.montantSteg !== null ? dossierForm.montantSteg : ''}
                      onChange={(e) => setDossierForm({...dossierForm, montantSteg: e.target.value})}
                      disabled={dossierForm.pompage}
                    >
                      <option value="0">Sélectionner le crédit STEG (0 DT - Comptant)</option>
                      {getStegOptionsList(dossierForm.montantTotalPaiement, dossierForm.montantSteg).map(opt => (
                        <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="premium-form-group">
                    <label className="premium-label">Autofinancement (Calculé DT)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="premium-input"
                      value={repartitionPreview.montantAutofinancement}
                      readOnly
                      style={{ backgroundColor: '#f3f4f6', color: '#1f2937', fontWeight: '600' }}
                    />
                  </div>

                  {/* Checkbox Acompte versé */}
                  <div className="premium-form-group premium-form-group-full" style={{ marginTop: '4px', marginBottom: '4px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '600', color: '#1f2937' }}>
                      <input
                        type="checkbox"
                        name="acompteVerse"
                        checked={!!dossierForm.acompteVerse}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setDossierForm(prev => ({
                            ...prev,
                            acompteVerse: checked,
                            montantComptant: checked ? prev.montantComptant : '',
                            datePaiementComptant: checked ? (prev.datePaiementComptant || getTodayStr()) : getTodayStr(),
                            modePaiementComptant: checked ? (prev.modePaiementComptant || 'especes') : 'especes'
                          }));
                        }}
                        style={{ width: '18px', height: '18px', accentColor: '#2563eb', cursor: 'pointer' }}
                      />
                      <span>Acompte versé</span>
                    </label>
                  </div>

                  {/* Champs conditionnels Acompte */}
                  {dossierForm.acompteVerse && (
                    <>
                      <div className="premium-form-group">
                        <label className="premium-label">Montant de l'acompte (TND) *</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="premium-input"
                          step="any"
                          min="0"
                          value={dossierForm.montantComptant}
                          onChange={(e) => setDossierForm({...dossierForm, montantComptant: normalizeDecimalInput(e.target.value)})}
                          placeholder="ex: 2000.000"
                          required
                          style={dossierFieldErrors.montantComptant ? { border: '1px solid #ef4444' } : {}}
                        />
                        {dossierFieldErrors.montantComptant && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{dossierFieldErrors.montantComptant}</span>}
                      </div>

                      <div className="premium-form-group">
                        <label className="premium-label">Date de l'acompte *</label>
                        <input
                          type="date"
                          className="premium-input"
                          value={dossierForm.datePaiementComptant || ''}
                          onChange={(e) => setDossierForm({...dossierForm, datePaiementComptant: e.target.value})}
                          required
                          style={dossierFieldErrors.datePaiementComptant ? { border: '1px solid #ef4444' } : {}}
                        />
                        {dossierFieldErrors.datePaiementComptant && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{dossierFieldErrors.datePaiementComptant}</span>}
                      </div>

                      <div className="premium-form-group">
                        <label className="premium-label">Mode de paiement de l'acompte *</label>
                        <select
                          className="premium-input premium-select"
                          value={dossierForm.modePaiementComptant}
                          onChange={(e) => setDossierForm({...dossierForm, modePaiementComptant: e.target.value})}
                          required
                          style={dossierFieldErrors.modePaiementComptant ? { border: '1px solid #ef4444' } : {}}
                        >
                          <option value="especes">Espèces</option>
                          <option value="cheque">Chèque</option>
                          <option value="traite">Traite bancaire</option>
                          <option value="virement">Virement bancaire</option>
                          <option value="versement_bancaire">Versement bancaire</option>
                          <option value="carte">Carte bancaire</option>
                          <option value="autre">Autre mode</option>
                        </select>
                        {dossierFieldErrors.modePaiementComptant && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{dossierFieldErrors.modePaiementComptant}</span>}
                      </div>
                    </>
                  )}

                  <div className="premium-form-group" style={{ position: 'relative' }}>
                    <label className="premium-label">Commercial</label>
                    <div style={{position:'relative'}}>
                      <input
                        type="text"
                        placeholder="🔍 Rechercher un commercial..."
                        value={commercialAddSearch}
                        onChange={(e) => {
                          setCommercialAddSearch(e.target.value);
                          if (!e.target.value) setDossierForm(prev => ({ ...prev, commercial: '' }));
                        }}
                        className="premium-input"
                        style={{width:'100%', padding:'10px 14px', boxSizing:'border-box'}}
                      />
                      {dossierForm.commercial && (
                        <span
                          style={{position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)', color:'#10b981', fontWeight:'700', fontSize:'16px', cursor:'pointer', lineHeight:1}}
                          onClick={() => { setDossierForm(prev => ({ ...prev, commercial: '' })); setCommercialAddSearch(''); }}
                          title="Effacer"
                        >
                          ✕
                        </span>
                      )}
                      {commercialAddSearch && !dossierForm.commercial && (
                        <div style={{position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1.5px solid #3b82f6', borderTop:'none', borderRadius:'0 0 8px 8px', maxHeight:'200px', overflowY:'auto', zIndex:300, boxShadow:'0 4px 16px rgba(0,0,0,0.12)'}}>
                          <div
                            style={{padding:'9px 14px', cursor:'pointer', fontSize:'13px', color:'#6b7280', borderBottom:'1px solid #f1f5f9'}}
                            onClick={() => { setDossierForm(prev => ({ ...prev, commercial: '' })); setCommercialAddSearch(''); }}
                          >
                            — Aucun commercial —
                          </div>
                          {commerciauxList
                            .filter(c => `${c.prenom} ${c.nom}`.toLowerCase().includes(commercialAddSearch.toLowerCase()))
                            .map(c => (
                              <div
                                key={c._id}
                                style={{padding:'9px 14px', cursor:'pointer', fontSize:'13px', color:'#1e293b', borderBottom:'1px solid #f9fafb'}}
                                onMouseEnter={e => e.currentTarget.style.background='#f0f9ff'}
                                onMouseLeave={e => e.currentTarget.style.background='transparent'}
                                onClick={() => { setDossierForm(prev => ({ ...prev, commercial: c._id })); setCommercialAddSearch(`${c.prenom} ${c.nom}`); }}
                              >
                                <strong>{c.prenom} {c.nom}</strong>
                                {c.numeroTelephone && <span style={{marginLeft:'8px', fontSize:'11px', color:'#6b7280'}}>{c.numeroTelephone}</span>}
                              </div>
                            ))
                          }
                          {commerciauxList.filter(c => `${c.prenom} ${c.nom}`.toLowerCase().includes(commercialAddSearch.toLowerCase())).length === 0 && (
                            <div style={{padding:'9px 14px', fontSize:'12px', color:'#9ca3af', fontStyle:'italic'}}>Aucun résultat pour "{commercialAddSearch}"</div>
                          )}
                        </div>
                      )}
                    </div>
                    {dossierForm.commercial && (
                      <small style={{color:'#10b981', fontSize:'0.78rem', marginTop:'4px', display:'block'}}>
                        ✓ Sélectionné : {commerciauxList.find(c => String(c._id) === String(dossierForm.commercial)) ? `${commerciauxList.find(c => String(c._id) === String(dossierForm.commercial)).prenom} ${commerciauxList.find(c => String(c._id) === String(dossierForm.commercial)).nom}` : ''}
                      </small>
                    )}
                  </div>

                  <div className="premium-form-group">
                    <label className="premium-label">Chef d'équipe (Installateur)</label>
                    <select
                      className="premium-input premium-select"
                      value={dossierForm.installateur}
                      onChange={(e) => setDossierForm({...dossierForm, installateur: e.target.value})}
                    >
                      <option value="">— Aucun chef d'équipe —</option>
                      {installateursList.map(i => (
                        <option key={i._id} value={i._id}>
                          {i.prenom || ''} {i.nom || ''} {i.isEquipe ? `(Équipe: ${i.typeEquipe})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="premium-form-group premium-form-group-full">
                    <label className="premium-label">Notes</label>
                    <textarea
                      className="premium-input premium-textarea"
                      value={dossierForm.notes}
                      onChange={(e) => setDossierForm({...dossierForm, notes: e.target.value})}
                      rows="3"
                      placeholder="Notes additionnelles..."
                    />
                  </div>
                </div>

                <div className="premium-modal-footer">
                  <button type="button" className="premium-btn-secondary" onClick={() => setShowAddDossierModal(false)}>
                    Annuler
                  </button>
                  <button type="submit" className="premium-btn-primary">
                    Créer le Dossier
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL CRÉER / MODIFIER PROJET */}
        {showProjectModal && (
          <div className="premium-modal-overlay">
            <div className="premium-modal" onClick={(e) => e.stopPropagation()} style={{maxWidth:'560px'}}>
              <div className="premium-modal-header">
                <h2>{editingProject ? '✏️ Modifier le projet' : '+ Nouveau Projet MT'}</h2>
                <button className="premium-modal-close" onClick={() => setShowProjectModal(false)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <form onSubmit={handleSubmitProject} className="premium-modal-body">
                <div className="premium-form-grid">
                  <div className="premium-form-group" style={{gridColumn:'1 / -1'}}>
                    <label className="premium-label">Nom du projet *</label>
                    <input
                      type="text"
                      className="premium-input"
                      value={projectForm.nomProjet}
                      onChange={(e) => setProjectForm({...projectForm, nomProjet: e.target.value})}
                      placeholder="Ex: Installation toiture 2024"
                      required
                      autoFocus
                    />
                  </div>
                  <div className="premium-form-group" style={{gridColumn:'1 / -1'}}>
                    <label className="premium-label">Description</label>
                    <textarea
                      className="premium-input premium-textarea"
                      value={projectForm.description}
                      onChange={(e) => setProjectForm({...projectForm, description: e.target.value})}
                      rows="3"
                      placeholder="Description optionnelle du projet..."
                    />
                  </div>
                  <div className="premium-form-group">
                    <label className="premium-label">Puissance Totale Prévue (kWc)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="premium-input"
                      value={projectForm.totalPuissance}
                      onChange={(e) => setProjectForm({...projectForm, totalPuissance: e.target.value})}
                      placeholder="Ex: 100"
                    />
                  </div>
                  <div className="premium-form-group">
                    <label className="premium-label">Montant Total Prévu (TND)</label>
                    <input
                      type="number"
                      step="0.001"
                      className="premium-input"
                      value={projectForm.totalMontant}
                      onChange={(e) => setProjectForm({...projectForm, totalMontant: e.target.value})}
                      placeholder="Ex: 150000"
                    />
                  </div>
                </div>
                <div className="premium-modal-footer" style={{marginTop:'20px',display:'flex',justifyContent:'flex-end',gap:'10px',borderTop:'1px solid #e2e8f0',paddingTop:'15px'}}>
                  <button type="button" className="premium-btn-secondary" onClick={() => setShowProjectModal(false)}>
                    Annuler
                  </button>
                  <button type="submit" className="premium-btn-primary">
                    {editingProject ? 'Enregistrer' : 'Créer le projet'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL AFFECTER DOSSIER AU PROJET */}
        {showAssignProjectModal && (
          <div className="premium-modal-overlay">
            <div className="premium-modal" onClick={(e) => e.stopPropagation()} style={{maxWidth:'500px'}}>
              <div className="premium-modal-header" style={{background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white'}}>
                <h2 style={{color: 'white'}}>📁 Affecter au Projet</h2>
                <button className="premium-modal-close" onClick={() => setShowAssignProjectModal(false)} style={{color: 'white'}}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <form onSubmit={handleAssignProject} className="premium-modal-body" style={{padding: '25px'}}>
                <p style={{marginBottom: '20px', color: '#4b5563', fontSize: '14px'}}>
                  Sélectionnez le projet auquel vous souhaitez rattacher le dossier <strong>{selectedDossierForProject?.referenceSteg || 'Pompage'}</strong>.
                </p>
                <div className="premium-form-group">
                  <label className="premium-label">Projet MT</label>
                  <select
                    className="premium-input premium-select"
                    value={assignmentProjectId}
                    onChange={(e) => setAssignmentProjectId(e.target.value)}
                  >
                    <option value="">— Aucun projet (Détacher) —</option>
                    {projects.map(p => (
                      <option key={p._id} value={p._id}>{p.nomProjet}</option>
                    ))}
                  </select>
                </div>
                <div className="premium-modal-footer" style={{marginTop:'30px',display:'flex',justifyContent:'flex-end',gap:'10px',borderTop:'1px solid #e2e8f0',paddingTop:'20px'}}>
                  <button type="button" className="premium-btn-secondary" onClick={() => setShowAssignProjectModal(false)}>
                    Annuler
                  </button>
                  <button type="submit" className="premium-btn-primary" style={{background: '#6366f1'}}>
                    Confirmer l'affectation
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL DÉTAILS PROJET (LISTE DOSSIERS SOUS FORME DE CARDS) */}
        {showProjectDetailModal && selectedProjectForDetail && (
          <div className="premium-modal-overlay">
            <div className="premium-modal" onClick={(e) => e.stopPropagation()} style={{maxWidth:'1000px', width:'95%'}}>
              <div className="premium-modal-header" style={{background: 'linear-gradient(135deg, #4338ca 0%, #312e81 100%)', color: 'white'}}>
                <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                  <div>
                    <h2 style={{color: 'white', margin:0}}>{selectedProjectForDetail.nomProjet}</h2>
                    <p style={{fontSize:'12px', opacity:0.8, margin:0}}>Détails du projet et dossiers rattachés</p>
                  </div>
                </div>
                <button className="premium-modal-close" onClick={() => setShowProjectDetailModal(false)} style={{color: 'white'}}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div className="premium-modal-body" style={{background: '#f8fafc', padding: '30px'}}>
                {/* Résumé Projet */}
                <div className="premium-info-card" style={{marginBottom:'30px', border:'1px solid #e2e8f0'}}>
                  <div className="info-card-body" style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:'20px'}}>
                    <div className="info-row" style={{flexDirection:'column', alignItems:'flex-start'}}>
                      <span className="info-label">Puissance Prévue</span>
                      <span className="info-value" style={{fontSize:'18px', color:'#4338ca'}}>{selectedProjectForDetail.totalPuissance || 0} kWc</span>
                    </div>
                    <div className="info-row" style={{flexDirection:'column', alignItems:'flex-start'}}>
                      <span className="info-label">Montant Prévu</span>
                      <span className="info-value" style={{fontSize:'18px', color:'#4338ca'}}>{(selectedProjectForDetail.totalMontant || 0).toLocaleString()} DT</span>
                    </div>
                    <div className="info-row" style={{flexDirection:'column', alignItems:'flex-start'}}>
                      <span className="info-label">Description</span>
                      <span className="info-value" style={{fontSize:'14px', fontStyle:'italic'}}>{selectedProjectForDetail.description || 'Aucune description'}</span>
                    </div>
                  </div>
                </div>

                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
                  <h3 style={{margin:0, color:'#1e293b', display:'flex', alignItems:'center', gap:'8px'}}>
                    📁 Dossiers rattachés
                    <span style={{background:'#e0e7ff', color:'#4338ca', padding:'2px 10px', borderRadius:'12px', fontSize:'12px'}}>
                      {(() => {
                        const dossiers = (client.dossiers || []).filter(d => !d.isDeleted && d.projectId && (d.projectId._id || d.projectId).toString() === selectedProjectForDetail._id.toString());
                        return dossiers.length;
                      })()}
                    </span>
                  </h3>
                  <button 
                    className="premium-btn-add" 
                    onClick={() => { setShowProjectDetailModal(false); handleOpenAddDossierToProject(selectedProjectForDetail._id); }}
                  >
                    + Nouveau Dossier
                  </button>
                </div>

                <div className="dossiers-premium-grid">
                  {(() => {
                    const dossiersProjet = (client.dossiers || []).filter(
                      d => !d.isDeleted && d.projectId &&
                           (d.projectId._id || d.projectId).toString() === selectedProjectForDetail._id.toString()
                    );

                    if (dossiersProjet.length === 0) {
                      return (
                        <div className="premium-empty-state" style={{gridColumn:'1/-1', background:'white'}}>
                          <p>Aucun dossier rattaché à ce projet.</p>
                        </div>
                      );
                    }

                    return dossiersProjet.map((dossier) => (
                      <div key={dossier._id} className="premium-dossier-card" style={dossier.pompage ? {borderLeft: '4px solid #0284c7'} : {}}>
                        <div className="dossier-card-header">
                          <div className="dossier-ref" style={{fontWeight:'700'}}>{dossier.referenceSteg || 'Pompage'}</div>
                          <span className="premium-badge" style={getEtatDossierStyle(dossier.etatDossier)}>
                            {dossier.etatDossier || '—'}
                          </span>
                        </div>
                        <div className="dossier-card-body">
                          <div className="dossier-detail-row">
                            <span className="detail-label">Puissance</span>
                            <span className="detail-value detail-value-accent">{dossier.puissance} kWc</span>
                          </div>
                          <div className="dossier-detail-row">
                            <span className="detail-label">District</span>
                            <span className="detail-value">{dossier.district ? (typeof dossier.district === 'object' ? dossier.district.region : dossier.district) : '-'}</span>
                          </div>
                          <div className="dossier-detail-row">
                            <span className="detail-label">Montant</span>
                            <span className="detail-value" style={{fontWeight:'600'}}>{(dossier.montantTotalPaiement || 0).toLocaleString()} DT</span>
                          </div>
                        </div>
                        <div className="dossier-card-footer">
                          <button className="premium-btn-secondary" onClick={() => handleViewDossier(dossier._id)}>Voir</button>
                          {isDossierCancelable(dossier) && (
                            <button className="premium-btn-warning" onClick={() => handleCancelDossier(dossier)}>Annuler</button>
                          )}
                          <button className="premium-btn-danger" onClick={() => handleDeleteDossier(dossier._id)}>Suppr.</button>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
              <div className="premium-modal-footer">
                <button className="premium-btn-secondary" onClick={() => setShowProjectDetailModal(false)}>Fermer</button>
              </div>
            </div>
          </div>
        )}

        {/* ONGLET RÉCLAMATIONS CLIENT */}
        {activeTab === 'reclamations' && (
          <div className="premium-tab-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#1e3a5f', fontSize: '1.2rem', fontWeight: 800 }}>⚡ Réclamations du Client</h3>
                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.88rem' }}>Historique et suivi centralisé des problèmes signalés par ou pour ce client.</p>
              </div>
              {hasPermission('reclamations', 'create') && (
                <button
                  type="button"
                  onClick={() => setShowAddReclamationModal(true)}
                  style={{ background: '#1e3a5f', color: '#ffffff', border: 'none', padding: '10px 18px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  + Nouvelle Réclamation Client
                </button>
              )}
            </div>

            {clientReclamations.length === 0 ? (
              <div className="premium-empty-state" style={{ background: '#ffffff', padding: '40px', borderRadius: '12px', textAlign: 'center', border: '1px dashed #cbd5e1' }}>
                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📂</div>
                <h4 style={{ margin: 0, color: '#334155' }}>Aucune réclamation enregistrée</h4>
                <p style={{ margin: '6px 0 16px', color: '#64748b', fontSize: '0.88rem' }}>Toutes les réclamations liées à ce client apparaîtront ici.</p>
                {hasPermission('reclamations', 'create') && (
                  <button
                    type="button"
                    onClick={() => setShowAddReclamationModal(true)}
                    style={{ background: '#1e3a5f', color: '#ffffff', border: 'none', padding: '10px 18px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    + Créer une réclamation
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                {clientReclamations.map((rec) => (
                  <div key={rec._id} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontWeight: 800, color: '#2563eb', fontSize: '0.85rem' }}>{rec.reference || 'REC-2026'}</span>
                        <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700, background: rec.statut === 'resolved' ? '#dcfce7' : '#eff6ff', color: rec.statut === 'resolved' ? '#166534' : '#1d4ed8' }}>
                          {rec.statut?.toUpperCase()}
                        </span>
                      </div>
                      <h4 style={{ margin: '0 0 6px', fontSize: '0.95rem', fontWeight: 700, color: '#1e293b' }}>{rec.sujet}</h4>
                      <p style={{ margin: '0 0 10px', fontSize: '0.82rem', color: '#64748b', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {rec.message}
                      </p>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
                      <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>📅 {new Date(rec.createdAt).toLocaleDateString('fr-FR')}</span>
                      <button
                        type="button"
                        onClick={() => setSelectedReclamationDetail(rec)}
                        style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#1e3a5f', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}
                      >
                        👁️ Détails
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* MODAL CRÉATION RÉCLAMATION CLIENT */}
        {showAddReclamationModal && (
          <ReclamationFormModal
            clientId={client._id}
            onClose={() => setShowAddReclamationModal(false)}
            onSuccess={() => fetchClientReclamations()}
          />
        )}

        {/* MODAL DÉTAIL RÉCLAMATION */}
        {selectedReclamationDetail && (
          <ReclamationDetailModal
            reclamation={selectedReclamationDetail}
            onClose={() => setSelectedReclamationDetail(null)}
            onRefresh={() => fetchClientReclamations()}
            onNavigate={onNavigate}
            hasPermission={hasPermission}
          />
        )}

        {/* Modale de suppression sensible globale (dossier) */}
        <SensitiveDeleteModal
          isOpen={showSensitiveDeleteModal}
          onClose={() => setShowSensitiveDeleteModal(false)}
          onConfirm={handleConfirmSensitiveDelete}
          itemType="dossier"
          itemName={deleteTarget?.name || ''}
          deletionConfig={deletionConfig.dossier || { enabled: false, mode: 'SOFT_DELETE' }}
          loading={deleteLoading}
        />

      </div>
    </div>
  );
};

export default DetailClient;
